import React, { useState, useContext, useEffect, useRef } from 'react'
import {
  Divider,
  Container,
  Grid,
  Radio,
  FormControlLabel,
  RadioGroup,
  Button,
  ButtonGroup,
  Tabs,
  Tab,
  Paper,
  Typography,
} from '@material-ui/core'
import { Apple, CreditCard as Credit, ErrorOutline } from '@material-ui/icons'
import UserProfileContext from '../../context/UserProfileContext'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import BrokerConfigService from '../../services/BrokerConfigService'
import Cards from 'react-credit-cards'
import 'react-credit-cards/es/styles-compiled.css'
import './CreditCard.scss'
import FlexView from 'react-flexview/lib'
import { SessionContextStore } from '../../context/SessionContext'
import { EventEmitterService, EventKey } from '../../services/EventEmitterService'
import { awaitWithLoading, awaitWithLoadingStripe, currencySymbol, isInFlow, isToday } from '../../services/utils'
import { BrowserView, MobileView } from 'react-device-detect'
import { TextDivider } from '../Core/TextDivier'
import { useLocation } from 'react-router-dom'
import { SessionService } from '../../services/SessionService'
import { BuyingInsuranceContextStore, methodsToFlags } from '../../context/BuyingInsuranceContext'
import { SkyPaper } from '../Core/Frames/SkyPaper'
import { BlueGrayBox } from '../Core/BlueGrayBox'
import { ReactComponent as CCSvg } from '../../assets/cc.svg'
import coveragesApi from '../../api/coverages.api'
import { useForm } from 'react-hook-form'
import { BrokerIdentityCode } from '../Core/BrokerIdentityCode'
import { enhancedCoversionEvent, reportGTM } from '../Core/Utilities/GTagManger'
import { isMobile } from 'react-device-detect'
import { flowType, PAYMENT_FEE, PAYMENT_METHODS } from '../../Constants'
import CheckoutForm from './Debit/CheckoutForm.js'
import CreditCardMethods from './CreditCardMethods.js'
import { BackButton } from '../Core/Buttons/BackButton.js'
import { MVRMessage } from '../Insurance/SummarySection/Mvr'
import chargesApi from '../../api/charges.api'
import { StatesContextStore } from '../../context/StatesContext'
import { SkyInformationPopover } from '../Core/Popover/SkyPopover'

let classNames = require('classnames')

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

export const CreditCard = () => {
  const [pollingInterval, setPollingInterval] = useState(null)
  const offersContext = useContext(BuyingInsuranceContextStore)
  const [offerId, setOfferId] = useState(offersContext.offerId)
  const latestOfferId = useRef(offerId)
  const location = useLocation()
  const sessionContext = React.useContext(SessionContextStore)
  const statesContext = React.useContext(StatesContextStore)
  const profile = useContext(UserProfileContext)
  const { getValues, register, triggerValidation, errors } = useForm({ mode: 'onChange' })
  let isPolicyStartingToday = isToday(offersContext.subscriptionStartTime)
  const userVehicles = offersContext.vehiclesDetailes
  let additionalFees = offersContext.pricebreakdown.additionalFees
  const minimumEarnedPremium = offersContext.pricebreakdown.minimumEarnedPremium

  let initialPerMileCharge =
    offersContext.billingFlowType === flowType.ESTIMATED_MILEAGE
      ? offersContext.pricebreakdown.totalEstimatedMileageCharge
      : offersContext.pricebreakdown.totalBaseMilesCharge

  const paymentFee = statesContext.no_initial_payment_fee.includes(sessionContext.user.administrativeDivision) ? 0 : PAYMENT_FEE
  const newEstimatedFlow = statesContext.new_estimated_flow.includes(sessionContext.user.administrativeDivision)

  let addOnPrice = 0
  if (offersContext.pricebreakdown.addOnCoveragePrices) {
    offersContext.pricebreakdown.addOnCoveragePrices.forEach(x => (addOnPrice += x.fixedPrice))
  }
  let total =
    initialPerMileCharge + paymentFee + offersContext.pricebreakdown.fixedPrice + additionalFees + addOnPrice + minimumEarnedPremium
  if (!sessionContext.newPaymentFlow) {
    total = offersContext.pricebreakdown.fixedPrice + paymentFee + additionalFees + minimumEarnedPremium
  }

  useEffect(() => {
    setOfferId(offersContext.offerId)
    latestOfferId.current = offersContext.offerId
  }, [offersContext.offerId])

  useEffect(() => {
    awaitWithLoadingStripe(stripePromise)
  }, [])

  const onPurchase = async () => {
    if (SessionService.isAgencyRequiresIdentityCode()) {
      let valid = await triggerValidation()
      if (!valid) return
    }
    if (!isInFlow(location.pathname)) return

    if (offersContext.offerId) {
      const external_user_id = getValues('external_user_id')
      let res = await awaitWithLoading(
        coveragesApi.bindOffer(
          latestOfferId.current,
          offersContext.signature,
          offersContext.offerCardWarnings.violationsId,
          external_user_id,
          isMobile
        )
      )
      if (res.ok) {
        const { insurance_id, should_check_status } = res.data
        if (should_check_status) {
          EventEmitterService.dispatch(EventKey.ShowLoader)
          const checkStatus = async () => {
            const res = await chargesApi.checkChargeStatus(insurance_id)
            const { data: status } = res
            switch (status) {
              case 'pending':
                setTimeout(checkStatus, 100)
                break
              case 'succeeded':
                enhancedCoversionEvent(sessionContext.user.email, sessionContext.user.phoneNumber, 'successful-payment-saved')
                EventEmitterService.dispatch(EventKey.DismissLoader)
                EventEmitterService.dispatch(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, res.data)
                break
              case 'requiresCapture':
                enhancedCoversionEvent(sessionContext.user.email, sessionContext.user.phoneNumber, 'hold_payment_failed')
                reportGTM('', '', { event: 'cc-failure', 'payment-method': 'cc' })
                EventEmitterService.dispatch(EventKey.DismissLoader)
                EventEmitterService.dispatch(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, res.data)
                break
              case 'failed':
                EventEmitterService.dispatch(EventKey.DismissLoader)
                EventEmitterService.dispatch(EventKey.ShowError, {
                  customError: {
                    title: 'Payment failed',
                    message: sessionContext.newPaymentFlow
                      ? 'We were not able to charge the added card; please try again or use a different card / payment method'
                      : 'We were not able to place a hold  for the added card; please try again or use a different card / payment method',
                  },
                  actionText: 'Continue',
                  onClose: () => console.log('first'),
                })
                enhancedCoversionEvent(
                  sessionContext.user.email,
                  sessionContext.user.phoneNumber,
                  !sessionContext.newPaymentFlow ? 'hold_payment_failed' : 'cc-failed'
                )
                break
            }
          }
          setTimeout(async () => {
            await checkStatus()
          }, 100)
        } else {
          enhancedCoversionEvent(sessionContext.user.email, sessionContext.user.phoneNumber, 'successful-payment-saved')
          EventEmitterService.dispatch(EventKey.FLOW_INSURANCE_PURCHASED_SUCCESS, res.data)
        }
      } else {
        if (res.data && res.data.messages && res.data.messages.haveCcFailure) {
          reportGTM('', '', { event: 'purchase_post_failed-payment_cancel' })
        }
        EventEmitterService.dispatch(EventKey.ShowError, res)
      }
    }
  }

  return (
    <React.Fragment>
      <MobileView>
        {location.pathname.includes('credit-card') && isInFlow(location.pathname) && <MVRMessage />}
        {isInFlow(location.pathname) && location.pathname.includes('credit-card') && (
          <div className="margin-side">
            <TextDivider>
              <div className="text-title bold blue">
                {isPolicyStartingToday || newEstimatedFlow
                  ? 'Due Today'
                  : ` Due on ${offersContext.subscriptionStartTime.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}`}
              </div>
            </TextDivider>
            <FlexView className="space-between margin-top-small">
              <div className="black text-body">Base rate</div>
              <div className="black text-body">
                {currencySymbol()}
                {offersContext.pricebreakdown.fixedPrice.toFixed(2)}
              </div>
            </FlexView>
            {sessionContext.newPaymentFlow && (
              <FlexView className="space-between margin-top-small">
                <div className="black text-body">Initial per mile charge</div>
                <div className="black text-body">
                  {currencySymbol()}
                  {initialPerMileCharge.toFixed(2)}
                </div>
              </FlexView>
            )}
            {!!paymentFee && (
              <FlexView className="space-between margin-top-small">
                <div className="black text-body">Installment fee</div>
                <div className="black text-body">
                  {currencySymbol()}
                  {paymentFee.toFixed(2)}
                </div>
              </FlexView>
            )}
            {additionalFees != 0 && (
              <FlexView className="space-between margin-top-small">
                <div className="black text-body">Anti-Theft prevention fee</div>
                <div className="black text-body">
                  {currencySymbol()}
                  {additionalFees.toFixed(2)}
                </div>
              </FlexView>
            )}
            {offersContext.pricebreakdown.addOnCoveragePrices
              .filter(coverage => coverage.fixedPrice != 0)
              .map((coverage, key) => (
                <FlexView className="space-between margin-top-small" key={key}>
                  <>
                    <div className="black text-body">{coverage.title}</div>
                    <div className="black text-body">
                      {currencySymbol()}
                      {coverage.fixedPrice.toFixed(2)}
                    </div>
                  </>
                </FlexView>
              ))}

            {minimumEarnedPremium != 0 && (
              <FlexView className="space-between margin-top-small">
                <div className="black text-body">
                  Minimum premium charge
                  <SkyInformationPopover style={{ fontSize: '0.9rem' }}>
                    <div className="text-small">
                      This partial charge is exclusively allocated to meet the minimum requirement for writing a new policy. By opting for
                      this payment, future installments will be deducted until the minimum is fully paid. The credit ensures the completion
                      of the necessary amount for policy creation.
                    </div>
                  </SkyInformationPopover>
                </div>
                <div className="black text-body">
                  {currencySymbol()}
                  {minimumEarnedPremium.toFixed(2)}
                </div>
              </FlexView>
            )}

            <FlexView hAlignContent="right" className="payment-summary-sum" />
            <FlexView className="space-between margin-top-small">
              <div className="black text-body bold">
                {isPolicyStartingToday || newEstimatedFlow
                  ? 'Total due today'
                  : `Total due on ${offersContext.subscriptionStartTime.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}`}{' '}
              </div>
              <div className="black text-body">
                {currencySymbol()}
                {total.toFixed(2)}
              </div>
            </FlexView>
            <Typography variant="body2" className="text-tiny margin-top-10 grey"></Typography>
          </div>
        )}
        <div className="margin-top-medium">
          <TextDivider>
            <div className="text-title bold blue">Payment Details</div>
          </TextDivider>
        </div>
      </MobileView>
      <Container maxWidth="md" className="margin-top-medium">
        <BrowserView>
          <FlexView vAlignContent={'center'} className="space-between">
            <FlexView vAlignContent={'center'}>
              <CCSvg className="margin-right-6 medium-icon" />
              <div className="blue text-title bold">Payment Details</div>
            </FlexView>
            <img className="strip-powered" src={`${process.env.PUBLIC_URL}/assets/stripe.png`} />
          </FlexView>
          <Divider />
        </BrowserView>
        <MobileView>
          <FlexView className="space-between">
            <div className="black text-title margin-bottom-basic">Add credit card</div>
            <img className="strip-powered" src={`${process.env.PUBLIC_URL}/assets/stripe.png`} />
          </FlexView>
        </MobileView>
        <Elements stripe={stripePromise}>
          <CreditCardMethods onPurchase={onPurchase} />
        </Elements>
        <Grid>
          {!sessionContext.newPaymentFlow && offersContext.paymentMethod !== 'Bank' && (
            <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
              <ErrorOutline className="purple margin-bottom-6" />
              <span className="text-small margin-left-3">
                Until your policy becomes active, we are placing a hold, through your credit card, for the amount of the first payment. In
                case the policy won’t be activated the hold will be released.
              </span>
            </Grid>
          )}
        </Grid>
      </Container>
      {SessionService.isAgencyRequiresIdentityCode() && (
        <BrokerIdentityCode
          className="margin-top-basic"
          name="external_user_id"
          register={register}
          helperText={errors.external_user_id && (errors.external_user_id.message || errors.external_user_id.type)}
          error={!!errors.external_user_id}
        />
      )}
      <BrowserView>
        {isInFlow(location.pathname) && (
          <>
            <Grid container className="margin-top-large">
              <BackButton />
            </Grid>
          </>
        )}
      </BrowserView>
    </React.Fragment>
  )
}
